"use client";

import { useCallback, useEffect, useState } from "react";
import { getDocs, limit, query } from "firebase/firestore";
import { v3GamesCol, v3OrganizationsCol } from "@/lib/v3/collections";
import type { User } from "firebase/auth";

const ONBOARDING_JUST_COMPLETED_KEY = "onboardingJustCompleted";

/** Custom event name dispatched by the welcome page after onboarding finishes. */
export const ONBOARDING_COMPLETED_EVENT = "onboarding-completed";

/**
 * Call this from the welcome/onboarding page after data has been written.
 * Sets the sessionStorage flag AND dispatches a DOM event so the
 * `useHasOnboardingData` hook updates immediately — even though `user`
 * hasn't changed and the hook's main effect won't re-run.
 */
export function signalOnboardingComplete() {
  if (typeof window !== "undefined") {
    sessionStorage.setItem(ONBOARDING_JUST_COMPLETED_KEY, "1");
    window.dispatchEvent(new Event(ONBOARDING_COMPLETED_EVENT));
  }
}

export type UseHasOnboardingDataResult = {
  /**
   * Whether the user has at least one game or organization in v3db.
   * - `true` / `false` when reads succeeded.
   * - `null` when the user is signed out, or after a failed read (unknown — do not treat as empty DB).
   */
  hasData: boolean | null;
  loading: boolean;
};

/**
 * Returns whether the user has completed onboarding (has games or orgs in v3db).
 * Used to gate the app shell / sidebar until onboarding is finished.
 * Honors sessionStorage flag set right after onboarding to avoid redirect loop.
 * Treats "has data" as: any games OR any organizations (so demo flow, which
 * seeds orgs but not games in V3, still counts as onboarded).
 *
 * On Firestore read failure, `loading` becomes false and `hasData` is `null` so
 * [`AppShellWrapper`](@/components/app-shell-wrapper.tsx) does not stay on the
 * logo forever and does not mis-route paying users via `hasData === false`.
 *
 * Depends only on `user` (not pathname) — onboarding status does not change on
 * navigation, and re-running the Firestore query on every route change caused
 * the loading gate to flash briefly on each navigation.
 */
export function useHasOnboardingData(user: User | null): UseHasOnboardingDataResult {
  const [hasData, setHasData] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);

  // Listen for the onboarding-completed event so we update immediately
  // even when `user` hasn't changed (the main effect won't re-fire).
  const handleOnboardingCompleted = useCallback(() => {
    setHasData(true);
    setLoading(false);
  }, []);

  useEffect(() => {
    window.addEventListener(ONBOARDING_COMPLETED_EVENT, handleOnboardingCompleted);
    return () => window.removeEventListener(ONBOARDING_COMPLETED_EVENT, handleOnboardingCompleted);
  }, [handleOnboardingCompleted]);

  useEffect(() => {
    if (!user) {
      setHasData(null);
      setLoading(false);
      return;
    }

    if (typeof window !== "undefined" && sessionStorage.getItem(ONBOARDING_JUST_COMPLETED_KEY)) {
      setHasData(true);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    const check = async () => {
      try {
        const [gamesSnap, orgsSnap] = await Promise.all([
          getDocs(query(v3GamesCol(user.uid), limit(1))),
          getDocs(query(v3OrganizationsCol(user.uid), limit(1))),
        ]);

        if (cancelled) return;
        if (typeof window !== "undefined" && sessionStorage.getItem(ONBOARDING_JUST_COMPLETED_KEY)) {
          setHasData(true);
          return;
        }
        setHasData(!gamesSnap.empty || !orgsSnap.empty);
      } catch (err) {
        console.error("[useHasOnboardingData] Failed to read games/organizations from v3db", err);
        if (!cancelled) {
          setHasData(null);
        }
      } finally {
        setLoading(false);
      }
    };

    void check();
    return () => {
      cancelled = true;
    };
  }, [user]);

  return { hasData, loading };
}
